import React, { Component, ErrorInfo, ReactNode } from 'react';
import { motion } from 'framer-motion';
import { ExclamationTriangleIcon, ArrowPathIcon, WifiIcon, CogIcon } from '@heroicons/react/24/outline';
import config from '../config/index';
import { FEATURE_FLAGS } from '../config/featureFlags';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
  errorType: 'general' | 'configuration' | 'network' | 'auth';
}

class ErrorBoundary extends Component<Props, State> {
  private retryTimeouts: NodeJS.Timeout[] = [];

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      errorType: 'general',
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Determine error type based on error message or type
    let errorType: State['errorType'] = 'general';
    
    if (error.message.toLowerCase().includes('configuration') || 
        error.message.toLowerCase().includes('environment')) {
      errorType = 'configuration';
    } else if (error.message.toLowerCase().includes('network') || 
               error.message.toLowerCase().includes('fetch') ||
               error.message.toLowerCase().includes('cors')) {
      errorType = 'network';
    } else if (error.message.toLowerCase().includes('auth') || 
               error.message.toLowerCase().includes('unauthorized')) {
      errorType = 'auth';
    }
    
    return { hasError: true, error, errorType };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to console in development - with sanitization check
    if (import.meta.env.DEV) {
      console.error('Error caught by boundary:', error, errorInfo);
    } else {
      // In production, only log non-technical error messages
      const technicalKeywords = ['undefined', 'null', 'logs', 'stacktrace'];
      const isTechnical = technicalKeywords.some(keyword =>
        error.message.toLowerCase().includes(keyword.toLowerCase())
      );

      if (isTechnical) {
        console.error('Technical error caught (sanitized):', error.constructor.name);
      } else {
        console.error('Error caught by boundary:', error.message);
      }
    }

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    this.setState({ errorInfo });
  }

  componentWillUnmount() {
    // Clear any pending retry timeouts
    this.retryTimeouts.forEach(clearTimeout);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: this.state.retryCount + 1,
    });
  };

  handleRetryWithBackoff = () => {
    const { retryCount } = this.state;
    const delay = Math.min(1000 * Math.pow(2, retryCount), 30000); // Max 30 seconds

    // Show loading state
    this.setState({ hasError: false });

    const timeout = setTimeout(() => {
      this.handleReset();
    }, delay);

    this.retryTimeouts.push(timeout);
  };

  private getErrorIcon() {
    switch (this.state.errorType) {
      case 'configuration':
        return <CogIcon className="w-8 h-8 text-orange-500 mr-3" />;
      case 'network':
        return <WifiIcon className="w-8 h-8 text-yellow-500 mr-3" />;
      case 'auth':
        return <ExclamationTriangleIcon className="w-8 h-8 text-purple-500 mr-3" />;
      default:
        return <ExclamationTriangleIcon className="w-8 h-8 text-red-500 mr-3" />;
    }
  }

  private getErrorTitle() {
    switch (this.state.errorType) {
      case 'configuration':
        return 'Configuration Error';
      case 'network':
        return 'Connection Error';
      case 'auth':
        return 'Authentication Error';
      default:
        return 'Something went wrong';
    }
  }

  private getErrorMessage() {
    switch (this.state.errorType) {
      case 'configuration':
        return 'The application is not properly configured. Please check your environment settings or contact support.';
      case 'network':
        return 'Unable to connect to our servers. Please check your internet connection and try again.';
      case 'auth':
        return 'Your session has expired or you need to sign in again.';
      default:
        return 'We encountered an unexpected error. The issue has been reported and we\'re working on a fix.';
    }
  }

  private getErrorActions() {
    const { errorType } = this.state;
    
    if (errorType === 'auth') {
      return (
        <>
          <button
            onClick={() => window.location.href = '/login'}
            className="flex-1 flex items-center justify-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
          >
            Sign In
          </button>
          <button
            onClick={this.handleReset}
            data-testid="retry-button"
            className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            <ArrowPathIcon className="w-5 h-5 mr-2 inline" />
            Retry
          </button>
        </>
      );
    }
    
    return (
      <>
        <button
          onClick={this.handleReset}
          data-testid="retry-button"
          className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          <ArrowPathIcon className="w-5 h-5 mr-2" />
          Try Again
        </button>
        <button
          onClick={() => window.location.href = '/'}
          className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          Go to Dashboard
        </button>
      </>
    );
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      // Default error UI
      return (
        <div 
          className="min-h-screen bg-gray-900 flex items-center justify-center px-4"
          data-testid="error-boundary"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-md w-full bg-gray-800 rounded-lg shadow-xl p-6 border border-red-500/20"
          >
            <div className="flex items-center mb-4">
              {this.getErrorIcon()}
              <h2 className="text-2xl font-bold text-white">{this.getErrorTitle()}</h2>
            </div>

            <div className="mb-6">
              <p className="text-gray-300 mb-2">
                {this.getErrorMessage()}
              </p>
              
              {/* Show configuration details in development for configuration errors */}
              {FEATURE_FLAGS.DEBUG_MODE_ENABLED && this.state.errorType === 'configuration' && (
                <div className="mt-4 p-3 bg-gray-900 rounded">
                  <p className="text-sm text-gray-400 mb-2">Environment: {config.environment.name}</p>
                  <p className="text-sm text-gray-400 mb-2">API URL: {config.api.baseUrl || 'Not configured'}</p>
                  <p className="text-sm text-gray-400">Version: {config.environment.version}</p>
                </div>
              )}
              
              {/* Detailed error in development */}
              {FEATURE_FLAGS.DEBUG_MODE_ENABLED && this.state.error && (
                <details className="mt-4">
                  <summary className="cursor-pointer text-gray-400 hover:text-gray-300">
                    Error details (development only)
                  </summary>
                  <pre className="mt-2 p-3 bg-gray-900 rounded text-xs text-red-400 overflow-auto">
                    {this.state.error.toString()}
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </details>
              )}
            </div>

            <div className="flex gap-3">
              {this.getErrorActions()}
            </div>

            {this.state.retryCount > 0 && (
              <p className="mt-4 text-sm text-gray-400 text-center">
                Retry attempt: {this.state.retryCount}
              </p>
            )}
          </motion.div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for functional components
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode
) {
  return (props: P) => (
    <ErrorBoundary fallback={fallback}>
      <Component {...props} />
    </ErrorBoundary>
  );
}

export default ErrorBoundary;